import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { transportProviderAPI } from '../../services/api';

const ROLE_LABEL = { owner: 'مالك', dispatcher: 'منسّق', driver: 'سائق' };

const inputCls = 'w-full border-2 border-gray-200 hover:border-blue-400/40 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 rounded-xl px-4 py-2.5 text-sm transition-all outline-none';
const labelCls = 'block text-xs font-bold text-gray-600 mb-1.5';

// Owner-only page (route itself doesn't gate on role — the backend already
// 403s a dispatcher/driver hitting these endpoints — but there's no reason
// to show the form to someone who can't use it).
const TransportProviderStaffPage = () => {
  const [staff, setStaff]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', role: 'driver' });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    transportProviderAPI.getStaff()
      .then((res) => setStaff(res.data.data || []))
      .catch(() => toast.error('فشل في تحميل الموظفين'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await transportProviderAPI.addStaff(form);
      toast.success('تم إضافة الموظف بنجاح');
      setShowForm(false);
      setForm({ name: '', email: '', phone: '', password: '', role: 'driver' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'تعذّرت الإضافة');
    } finally { setSaving(false); }
  };

  const toggle = async (staffId) => {
    try {
      await transportProviderAPI.toggleStaff(staffId);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'تعذّر التحديث');
    }
  };

  return (
    <div dir="rtl" className="p-4 sm:p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-black text-gray-800">👥 فريق العمل</h1>
        <button onClick={() => setShowForm((s) => !s)} className="bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold px-4 py-2 rounded-xl transition">
          {showForm ? 'إلغاء' : '+ إضافة موظف'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3 mb-6">
          <div><label className={labelCls}>الاسم الكامل *</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div><label className={labelCls}>البريد الإلكتروني *</label><input type="email" dir="ltr" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
          <div><label className={labelCls}>رقم الهاتف *</label><input dir="ltr" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required /></div>
          <div><label className={labelCls}>كلمة المرور *</label><input type="password" dir="ltr" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required /></div>
          <div>
            <label className={labelCls}>الدور</label>
            <select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="driver">سائق</option>
              <option value="dispatcher">منسّق</option>
            </select>
          </div>
          <button type="submit" disabled={saving} className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white py-2.5 rounded-xl font-bold transition">
            {saving ? '...جارٍ الحفظ' : 'إضافة'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-center text-gray-400 py-10">جاري التحميل...</div>
      ) : staff.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400">لا يوجد موظفون مضافون بعد</div>
      ) : (
        <div className="space-y-2">
          {staff.map((s) => (
            <div key={s.id} className="flex items-center justify-between bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <div>
                <p className="font-semibold text-sm text-gray-800">{s.user?.name}</p>
                <p className="text-xs text-gray-500">{s.user?.email} · {ROLE_LABEL[s.role] || s.role}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'}`}>
                  {s.isActive ? 'نشط' : 'موقوف'}
                </span>
                {s.role !== 'owner' && (
                  <button onClick={() => toggle(s.id)} className="text-xs text-gray-500 hover:text-gray-800 underline">
                    {s.isActive ? 'إيقاف' : 'تفعيل'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TransportProviderStaffPage;
